/**
 * adminLearningPathStore — Learning Path moderation slice of the admin store.
 *
 * Learning paths bundle several approved courses and are submitted by resource
 * persons for review, so an admin must be able to list, inspect, edit, approve
 * and reject them. These are the endpoints from the collection:
 *
 *   GET   /api/admin/learning-paths          — list for moderation
 *   GET   /api/admin/learning-paths/:id      — detail
 *   PATCH /api/admin/learning-paths/:id      — edit (title, description, …)
 *   PATCH /api/admin/learning-paths/:id/approve
 *   PATCH /api/admin/learning-paths/:id/reject   — { reason }
 */
import { API_URL } from "../../constants";
import { toDisplayString } from "../../utils/toDisplayString";
import { authHeaders, resolveToken } from "./adminUtils";

const DEFAULT_PAGINATION = { total: 0, totalPages: 1 };

export const learningPathInitialState = {
  learningPaths: [],
  learningPathsLoading: false,
  learningPathsError: null,
  learningPathsTotal: 0,
  learningPathsPages: 1,
  learningPathDetail: null,
  learningPathDetailLoading: false,
  learningPathDetailError: null,
  learningPathMutationLoading: false,
  learningPathMutationError: null,
};

/**
 * Normalize the varying list envelopes the API may return into
 * `{ items, total, totalPages }`.
 */
function normalizeListPayload(data) {
  if (!data || typeof data !== "object") return { items: [], ...DEFAULT_PAGINATION };

  let items = [];
  if (Array.isArray(data)) {
    items = data;
  } else if (Array.isArray(data.data)) {
    items = data.data;
  } else {
    const payload = data.data && typeof data.data === "object" ? data.data : data;
    items = payload.learningPaths || payload.items || data.items || [];
  }

  const pagination = data.pagination || {};
  return {
    items: Array.isArray(items) ? items : [],
    total: pagination.total ?? data.total ?? (Array.isArray(items) ? items.length : 0),
    totalPages: pagination.totalPages ?? pagination.pages ?? data.totalPages ?? data.pages ?? 1,
  };
}

/**
 * Authenticated JSON request that surfaces the API error envelope
 * (`{ error: { message } }`) as a thrown Error with a displayable message.
 * Mirrors the helper in adminAcademicResourceStore.
 */
async function apiRequest(get, path, { method = "GET", body } = {}) {
  const response = await fetch(`${API_URL}${path}`, {
    method,
    headers: authHeaders(resolveToken(get)),
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  const text = await response.text();
  let data = {};
  if (text) {
    const contentType = response.headers.get("content-type") || "";
    const looksJson = contentType.includes("application/json") ||
      text.trim().startsWith("{") || text.trim().startsWith("[");
    if (looksJson) {
      try {
        data = JSON.parse(text);
      } catch {
        data = {};
      }
    }
  }

  if (!response.ok || (data && data.error)) {
    const message = toDisplayString(
      data?.error ?? data?.message,
      `Request failed (${response.status})`
    );
    throw new Error(message);
  }

  return data;
}

export function createLearningPathActions(set, get) {
  /** Run an authenticated moderation mutation and return `{ success, error? }`. */
  async function mutate(path, method, body, successMessage) {
    set({ learningPathMutationLoading: true, learningPathMutationError: null });
    try {
      const data = await apiRequest(get, path, { method, body });
      set({
        learningPathMutationLoading: false,
        learningPathMutationError: null,
        successMessage,
      });
      return { success: true, data: data?.data ?? data };
    } catch (err) {
      const message = toDisplayString(err.message, "Action failed");
      set({ learningPathMutationLoading: false, learningPathMutationError: message });
      return { success: false, error: message };
    }
  }

  return {
    /** GET /api/admin/learning-paths — moderation queue. */
    fetchLearningPaths: async (params = {}) => {
      set({ learningPathsLoading: true, learningPathsError: null });
      try {
        const query = new URLSearchParams(
          Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
        ).toString();
        const data = await apiRequest(get, `/api/admin/learning-paths${query ? `?${query}` : ""}`);
        const { items, total, totalPages } = normalizeListPayload(data);
        set({
          learningPathsLoading: false,
          learningPaths: items,
          learningPathsTotal: total,
          learningPathsPages: totalPages,
        });
        return { success: true, items, total, totalPages };
      } catch (err) {
        const message = toDisplayString(err.message, "Failed to fetch learning paths");
        set({ learningPathsLoading: false, learningPathsError: message });
        return { success: false, error: message };
      }
    },

    /** GET /api/admin/learning-paths/:id */
    fetchLearningPathById: async (id) => {
      set({ learningPathDetailLoading: true, learningPathDetailError: null });
      try {
        const data = await apiRequest(get, `/api/admin/learning-paths/${id}`);
        const detail = data?.data?.learningPath ?? data?.learningPath ?? data?.data ?? data;
        set({ learningPathDetailLoading: false, learningPathDetail: detail });
        return { success: true, learningPath: detail };
      } catch (err) {
        const message = toDisplayString(err.message, "Failed to fetch learning path");
        set({ learningPathDetailLoading: false, learningPathDetailError: message });
        return { success: false, error: message };
      }
    },

    /** PATCH /api/admin/learning-paths/:id */
    updateLearningPath: async (id, payload = {}) => {
      const result = await mutate(
        `/api/admin/learning-paths/${id}`,
        "PATCH",
        payload,
        "Learning path updated successfully"
      );
      if (result.success) {
        // Reflect the change in the cached list/detail without a refetch.
        set((s) => ({
          learningPaths: s.learningPaths.map((lp) =>
            String(lp.id) === String(id) ? { ...lp, ...payload } : lp
          ),
          learningPathDetail:
            s.learningPathDetail && String(s.learningPathDetail.id) === String(id)
              ? { ...s.learningPathDetail, ...payload }
              : s.learningPathDetail,
        }));
      }
      return result;
    },

    /** PATCH /api/admin/learning-paths/:id/approve */
    approveLearningPath: (id) =>
      mutate(
        `/api/admin/learning-paths/${id}/approve`,
        "PATCH",
        undefined,
        "Learning path approved successfully"
      ),

    /** PATCH /api/admin/learning-paths/:id/reject — body carries the reason. */
    rejectLearningPath: (id, reason = "") =>
      mutate(
        `/api/admin/learning-paths/${id}/reject`,
        "PATCH",
        { reason },
        "Learning path rejected successfully"
      ),
  };
}

const learningPathActions = createLearningPathActions;
export default learningPathActions;
